import { Button } from "@mui/material"
import {
  getListPlatformsBiomeshPlatformListGetQueryKey,
  useUpdatePlatformBiomeshPlatformPlatformIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import type { BBox } from "geojson"
import { closeSnackbar, enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useRef, useState } from "react"
import type { MarkerDragEvent } from "react-map-gl/mapbox"
import { Marker, useMap } from "react-map-gl/mapbox"
import { isNodeType, toCentroid } from "../Cop/copUtils"
import { useBioMeshUIState } from "./BioMeshUIStateContext"
import { getCoordinates } from "./getCoordinates"
import { NodePieMarker } from "./NodePieMarker"
import type { ClusterOrPoint } from "./useNodeClusters"
import { isCluster, MAX_CLUSTER_ZOOM, useNodeClusters } from "./useNodeClusters"

/** Size in px for cluster markers (scaled by point count). */
const clusterSize = (count: number) => Math.min(28 + count * 2, 64)
const SINGLE_SIZE = 36

interface NodeLayerProps {
  onOpenDrawer: (nodeIds: string[]) => void
}

export const NodeLayer = ({ onOpenDrawer }: NodeLayerProps) => {
  const { current: mapRef } = useMap()
  const { getClusters, getExpansionZoom, getLeaves } = useNodeClusters()
  const [clusters, setClusters] = useState<ClusterOrPoint[]>([])
  const bound = useRef(false)
  const { isEditing, selectedNodeIds } = useBioMeshUIState()
  const queryClient = useQueryClient()
  const { mutateAsync: updatePlatform } =
    useUpdatePlatformBiomeshPlatformPlatformIdPut()

  // Optimistic position overrides so markers don't snap back after drag
  const [dragOverrides, setDragOverrides] = useState<
    Record<string, { lng: number; lat: number }>
  >({})

  const handleDragEnd = useCallback(
    async (
      platformId: string,
      name: string,
      platformType: string,
      origLng: number,
      origLat: number,
      evt: MarkerDragEvent
    ) => {
      const { lat, lng } = evt.lngLat

      if (!isNodeType(platformType)) {
        enqueueSnackbar("Unknown platform type", { variant: "error" })
        return
      }

      // Optimistically hold the new position
      setDragOverrides(prev => ({ ...prev, [platformId]: { lng, lat } }))

      const revert = async () => {
        setDragOverrides(prev => ({
          ...prev,
          [platformId]: { lng: origLng, lat: origLat },
        }))
        try {
          await updatePlatform({
            platformId,
            data: {
              name,
              platform_type: platformType,
              centroid: toCentroid(origLat, origLng),
            },
          })
          await queryClient.invalidateQueries({
            queryKey: getListPlatformsBiomeshPlatformListGetQueryKey(),
          })
          enqueueSnackbar("Move undone", { variant: "info" })
        } catch {
          enqueueSnackbar("Failed to undo move", { variant: "error" })
        } finally {
          setDragOverrides(prev => {
            const { [platformId]: _, ...next } = prev
            return next
          })
        }
      }

      try {
        await updatePlatform({
          platformId,
          data: {
            name,
            platform_type: platformType,
            centroid: toCentroid(lat, lng),
          },
        })
        await queryClient.invalidateQueries({
          queryKey: getListPlatformsBiomeshPlatformListGetQueryKey(),
        })
        enqueueSnackbar("Node location updated", {
          variant: "success",
          action: snackbarId => (
            <Button
              size="small"
              sx={{ color: "inherit", fontWeight: 700 }}
              onClick={() => {
                closeSnackbar(snackbarId)
                void revert()
              }}
            >
              Undo
            </Button>
          ),
        })
        // Clear the optimistic override now that the cache has been updated
        setDragOverrides(prev => {
          const { [platformId]: _, ...next } = prev
          return next
        })
      } catch {
        // Revert optimistic override on failure
        setDragOverrides(prev => ({
          ...prev,
          [platformId]: { lng: origLng, lat: origLat },
        }))
        enqueueSnackbar("Failed to update node location", {
          variant: "error",
        })
      }
    },
    [updatePlatform, queryClient]
  )

  const updateClusters = useCallback(() => {
    const map = mapRef?.getMap()
    if (!map) return
    const b = map.getBounds()
    if (!b) return
    const bbox: BBox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
    setClusters(getClusters(bbox, map.getZoom()))
  }, [mapRef, getClusters])

  useEffect(() => {
    const map = mapRef?.getMap()
    if (!map || bound.current) return
    bound.current = true
    map.on("moveend", updateClusters)
    // Initial + filter-change recomputation
    const frame = requestAnimationFrame(updateClusters)
    return () => {
      cancelAnimationFrame(frame)
      map.off("moveend", updateClusters)
      bound.current = false
    }
  }, [mapRef, updateClusters])

  return (
    <>
      {clusters.flatMap(c => {
        const coords = getCoordinates(c.geometry)
        if (!coords) return []
        const [lng, lat] = coords

        if (isCluster(c)) {
          const { cluster_id, point_count, fmc, pmc, nmc, total } = c.properties
          const size = clusterSize(point_count)
          const leaves = getLeaves(cluster_id)
          const nodeNames = leaves.map(l => l.properties.name)
          const clusterSelected = leaves.some(l =>
            selectedNodeIds.has(l.properties.platform_id)
          )
          return (
            <Marker
              key={`cluster-${cluster_id}`}
              longitude={lng}
              latitude={lat}
              anchor="center"
            >
              <NodePieMarker
                counts={{ fmc, pmc, nmc, total }}
                size={size}
                nodeCount={point_count}
                nodeNames={nodeNames}
                selected={clusterSelected}
                onClick={() => {
                  const zoom = getExpansionZoom(cluster_id)
                  // Co-located nodes can't expand further — open drawer
                  if (zoom >= MAX_CLUSTER_ZOOM) {
                    onOpenDrawer(leaves.map(l => l.properties.platform_id))
                    return
                  }
                  mapRef?.flyTo({
                    center: [lng, lat],
                    zoom: zoom + 1,
                    duration: 500,
                  })
                }}
              />
            </Marker>
          )
        }

        // Single node point
        const { platform_id, name, platform_type, fmc, pmc, nmc, total } =
          c.properties
        const pos = dragOverrides[platform_id]
        return (
          <Marker
            key={platform_id}
            longitude={pos?.lng ?? lng}
            latitude={pos?.lat ?? lat}
            anchor="center"
            draggable={isEditing}
            onDragEnd={(evt: MarkerDragEvent) =>
              void handleDragEnd(
                platform_id,
                name,
                platform_type,
                lng,
                lat,
                evt
              )
            }
          >
            <NodePieMarker
              counts={{ fmc, pmc, nmc, total }}
              size={SINGLE_SIZE}
              nodeNames={[name]}
              selected={selectedNodeIds.has(platform_id)}
              onClick={() => onOpenDrawer([platform_id])}
            />
          </Marker>
        )
      })}
    </>
  )
}
